/* eslint-disable react/prop-types */
import React, { useContext } from 'react';
import FocusTrap from 'focus-trap-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { flowRight } from 'lodash';
import {
  getActivityTypeTitle,
  getGoalTimeSpentInMinutes,
  getRecommendationsForAllGoals,
  getRequirementSubtitle
} from './remediation-student-activity.utilities';
import {
  ELSButton,
  ELSDataTable
} from '../../components/els.components';
import { AssessmentGoalDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { StatusPill } from '../../components/status-pill/StatusPill.component';
import { StatusDisplayMap } from '../../constants/remediation.constants';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import GoalRecommendationModal, { GoalRecommendationModalId } from './GoalRecommendationModal.component';
import {
  RecommendationDto,
  RemediationActivityTypeDto,
  StatusWithDueDate
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  filterGoalsByActivityType,
  getAssessmentGoalStatusWithDueDate,
  getAssessmentGoalTotalsCombined,
  getCategoryGroupNameForGoal,
  getContentTypeMainDisplay,
  getContentTypesFromActivityType,
  getContentTypeSubtitleDisplay,
  getGoalBreadcrumbs,
  getHourAndMinuteFromMinute,
  getMetricCounts,
  getProgressDisplay,
} from '../../utilities/remediation.utilities';
import { EmptyValueDisplay } from './remediation-student-activity.constants';
import {
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { AppContext } from '../../components/app/AppProvider.component';

export type ActivityProgressSectionPropsOnly = {
  activityType: RemediationActivityTypeDto;
  handleSelectGoal: (goal: AssessmentGoalDto) => void;
};

export type ActivityProgressSectionProps = ActivityProgressSectionPropsOnly & ELSPropsFromModalService;

// eslint-disable-next-line sonarjs/cognitive-complexity
const ActivityProgressSectionComponent = (props: ActivityProgressSectionProps) => {
  const { appState, appStateRef } = useContext(AppContext);
  const baseState = appState.remediationBaseState;

  if (!baseState || !baseState.eolsAssessment) {
    return null;
  }

  const {
    activityType
  } = props;
  const { assessmentGoals } = baseState.eolsAssessment;

  const handleOpenModal = (goalRecommendations: RecommendationDto[], assessmentGoal: AssessmentGoalDto) => {
    const {
      modalService,
    } = props;

    if (!assessmentGoal) {
      return;
    }

    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:viewActivities:${activityType}`,
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.VIEW_ACTIVITIES_CLICKED,
        props: {
          activityType,
          studentUserId: baseState.raasAssessment?.userId,
          studentStudyEolsAssessmentId: baseState.raasAssessment?.id,
          remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id
        }
      }
    });

    modalService.openCustomModal({
      modalId: GoalRecommendationModalId,
      modal: (
        <FocusTrap>
          <GoalRecommendationModal
            activityType={activityType}
            selectedGoal={assessmentGoal}
            categoryGroupName={getCategoryGroupNameForGoal(assessmentGoal, baseState)}
            goalRecommendations={goalRecommendations}
            modalService={modalService}
          />
        </FocusTrap>
      )
    });
  };

  const getGoalTitleTableHeaderByActivityType = (): string => {
    switch (activityType) {
      case RemediationActivityTypeDto.REVIEW_BY_TAXON:
        return 'Topic';
      case RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE:
        return 'Content Type';
      case RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE:
        return 'Content Type';
      default:
        return '';
    }
  };

  const getGoalStatusDisplay = (assessmentGoal: AssessmentGoalDto) => {
    const status = getAssessmentGoalStatusWithDueDate(assessmentGoal, baseState);
    return (
      <StatusPill status={status}>
        {StatusDisplayMap[status]}
      </StatusPill>
    );
  };

  const getActivitiesCompletedDisplay = (assessmentGoal: AssessmentGoalDto): string => {
    const statusCountsForGoal = getAssessmentGoalTotalsCombined({
      assessmentGoal,
      baseState,
    });

    return getProgressDisplay(
      statusCountsForGoal,
      activityType
    );
  };

  const isGoalInProgress = (assessmentGoal: AssessmentGoalDto): boolean => {
    const status = getAssessmentGoalStatusWithDueDate(assessmentGoal, baseState);
    return status === StatusWithDueDate.IN_PROGRESS;
  };

  const getGoalTimeSpentDisplay = (assessmentGoal: AssessmentGoalDto): string => {
    const timeSpentMinutes = getGoalTimeSpentInMinutes({
      recommendations: baseState.recommendations,
      recommendationAttempts: baseState.recommendationAttempts,
      attemptAssessmentSubmissions: baseState.attemptAssessmentSubmissions,
      assessmentGoal
    });

    if (isGoalInProgress(assessmentGoal) && timeSpentMinutes === 0) {
      return EmptyValueDisplay;
    }

    return getHourAndMinuteFromMinute(timeSpentMinutes);
  };

  const getStudentActivityViewBtn = (assessmentGoal: AssessmentGoalDto) => {
    return (
      <div className="u-els-text-right">
        <ELSButton
          type={ELSButtonType.SECONDARY}
          size={ELSButtonSize.SMALL}
          onClick={() => handleOpenModal(goalRecommendations, assessmentGoal)}
        >
          View activities
        </ELSButton>
      </div>
    );
  };

  const getGoalTitleDisplay = (assessmentGoal: AssessmentGoalDto) => {
    const statusCountsForGoal = getAssessmentGoalTotalsCombined({
      assessmentGoal,
      baseState,
    });

    if (!statusCountsForGoal) {
      return null;
    }

    const hasGoalBeenStarted = statusCountsForGoal.activityTypeTotalGoalCompleted > 0
      || statusCountsForGoal.activityTypeAttemptGoalCompleted > 0
      || isGoalInProgress(assessmentGoal);
    const goalBreadcrumbs = getGoalBreadcrumbs(assessmentGoal, getCategoryGroupNameForGoal(assessmentGoal, baseState));

    return (
      <>
        {
          goalBreadcrumbs.path.length > 0 && (
            <div className="u-els-margin-bottom-1o4">
              {goalBreadcrumbs.pathDisplay}
            </div>
          )
        }

        {
          !hasGoalBeenStarted && (
            <div>
              {goalBreadcrumbs.text}
            </div>
          )
        }

        {
          hasGoalBeenStarted && (
            <div>
              <button
                onClick={() => props.handleSelectGoal(assessmentGoal)}
                type="button"
                className="u-els-anchorize u-els-display-block"
              >
                {goalBreadcrumbs.text}
              </button>
            </div>
          )
        }

      </>
    );

  };

  const activityGoals = filterGoalsByActivityType(assessmentGoals, activityType);
  const goalRecommendations = getRecommendationsForAllGoals(
    baseState.recommendations,
    activityGoals
  );

  const metricCounts = getMetricCounts(baseState, activityType)
  const contentTypeDisplay = getContentTypeMainDisplay(getContentTypesFromActivityType(activityType, baseState), null);
  const contentTypeSubtitleDisplay = getContentTypeSubtitleDisplay(getContentTypesFromActivityType(activityType, baseState));

  return (
    <>
      {activityGoals && (
        <>
          <div className="u-els-margin-top-2x u-els-margin-bottom-1x">
            <div>
              <span className="u-els-font-size-body-large u-els-bold u-els-display-block">
                {getActivityTypeTitle(activityType, contentTypeDisplay)}
              </span>
            </div>
            <span className="u-els-color-n7">
              {getRequirementSubtitle(baseState.remediationAssignmentWithSources, assessmentGoals, activityType, metricCounts, contentTypeSubtitleDisplay)}
            </span>
          </div>

          <ELSDataTable
            data={activityGoals}
            noWrap
            sortIconSize="1o2"
            columnHeaderCss={['', '', '', '', 'u-els-text-right']}
            columns={[
              {
                header: getGoalTitleTableHeaderByActivityType(),
                field: 'text',
                customRender: (assessmentGoal: AssessmentGoalDto) => {
                  return getGoalTitleDisplay(assessmentGoal);
                }
              },
              {
                header: 'Status',
                field: 'vtwId',
                customRender: (assessmentGoal: AssessmentGoalDto) => getGoalStatusDisplay(assessmentGoal)
              },
              {
                header: 'Activities Completed',
                field: 'completed',
                customRender: (assessmentGoal: AssessmentGoalDto) => getActivitiesCompletedDisplay(assessmentGoal)
              },
              {
                header: 'Time Spent',
                field: 'timeSpent',
                customRender: (assessmentGoal: AssessmentGoalDto) => getGoalTimeSpentDisplay(assessmentGoal)
              },
              {
                header: ' ',
                customRender: (assessmentGoal: AssessmentGoalDto) => getStudentActivityViewBtn(assessmentGoal)
              }
            ]}
          />
        </>
      )}
    </>
  );
};

const enhance = flowRight(ELSWithModalService);

const ActivityProgressSection:React.FC<ActivityProgressSectionPropsOnly> = enhance(ActivityProgressSectionComponent);

export default ActivityProgressSection;
